import { KeyboardEvent, useRef } from 'react';
import { Image, useWindowSize } from '@marriott/mi-ui-library';
import {
  CenterContentGrid,
  ButtonRow,
  Card,
  Description,
  Title,
  StyledButton,
  EnrollmentCardContent,
  FloatingCardContent,
} from './EnrollmentCard.styles';
import type { EnrollmentCardProps } from './EnrollmentCard.types';
import clsx from 'clsx';
import { useImageLoadTracker } from '../../hooks';

// role="button" on <a> requires Space to activate (ARIA button pattern)
const handleSpaceAsClick = (e: KeyboardEvent<HTMLAnchorElement | HTMLButtonElement>) => {
  if (e.key === ' ' || e.code === 'Space') {
    e.preventDefault();
    (e.currentTarget as HTMLElement).click();
  }
};

export function EnrollmentCard({
  className,
  header,
  description,
  children,
  isLoggedInVariant,
  isLoading = false,
  buttons,
  image,
  theme,
}: EnrollmentCardProps) {
  const ecRef = useRef<HTMLDivElement>(null);
  const { width: viewportWidth } = useWindowSize();
  const hasResolvedViewport = typeof viewportWidth === 'number';
  const isPageLoading = !hasResolvedViewport;
  const hasImageData = Boolean(image?.fileReferenceImage) || Boolean(image?.dynamicMedia?.renditions?.length);
  const isImageLoaded = useImageLoadTracker(ecRef, [
    hasResolvedViewport,
    image?.fileReferenceImage,
    image?.dynamicMedia,
  ]);
  const isLoadingSlot = isLoading;
  const isCardLoading = isPageLoading || (isLoadingSlot && (!hasImageData || !isImageLoaded));
  const deferContentForAssetLoading = isLoadingSlot && isCardLoading;
  const shouldRenderImage = Boolean(isLoggedInVariant) && !isCardLoading && (hasResolvedViewport || !isLoadingSlot);
  return (
    <Card
      className={clsx(
        isLoggedInVariant && 'logged-in-variant',
        isCardLoading && 'skeleton-loader',
        deferContentForAssetLoading && 'page-loading',
        className
      )}
      $isLoggedInVariant={isLoggedInVariant}
      $theme={theme}
      data-testid="homepage-EnrollmentCard"
      role="region"
      aria-label={header}
      ref={ecRef}
    >
      {shouldRenderImage && image && hasImageData ? (
        <Image
          altText={image.altText}
          renditions={image.dynamicMedia?.renditions}
          defaultImageURL={image.fileReferenceImage}
          customClass="enrollment-image"
        />
      ) : null}
      {!deferContentForAssetLoading && (
        <FloatingCardContent>
          <EnrollmentCardContent>
            <Title $isLoggedInVariant={isLoggedInVariant}>{header}</Title>
            <Description $isLoggedInVariant={isLoggedInVariant} $theme={theme}>
              {description}
            </Description>
            {children ? (
              // role="list" on grid container so SR announces item count
              <CenterContentGrid $theme={theme} role="list">
                {children}
              </CenterContentGrid>
            ) : null}
            <ButtonRow>
              {buttons
                ?.filter(button => button.buttonCopy)
                .map((props, index) => (
                  <StyledButton
                    key={index}
                    {...props}
                    isLink={true}
                    {...({ role: 'button', onKeyDown: handleSpaceAsClick } as Record<string, unknown>)}
                  />
                ))}
            </ButtonRow>
          </EnrollmentCardContent>
        </FloatingCardContent>
      )}
    </Card>
  );
}
